// src/App.js

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// ========== CONTEXTS, STYLES, UTILS ==========
import { useAuth } from './contexts/AuthContext';
import useDeviceType from './components/useDeviceType';
import theme from './theme';
import GlobalStyles from './GlobalStyles';
import { LoadingContainer, LoadingBar } from './components/LoadingStyles';
import apiCall from './utils/api';
import AppLoadingHandler from './contexts/AppLoadingHandler';

// ========== LAYOUTS & ROUTE GUARDS ==========
import ProtectedRoute from './utils/ProtectedRoute';


// ========== EAGERLY-LOADED / PUBLIC COMPONENTS ==========
import LoginPage from './components/LoginPage';
import SignUp from './components/SignUp';
import FirstPage from './components/FirstPage';
import MeoFirstMessage from './components/MeoFirstMessage';
import FicheFormation from './components/FicheFormation';
import DesktopLandingPage from './components/Desktop/LandingPage';
import ManagerLogin from './components/Desktop/LandingPage/ManagerLogin';
import ManagerLandingPage from './components/Desktop/LandingPage/Manager';
import Analytics from './components/Manager/Analytics';
import Update from './components/Manager/Update';
import Profile from './components/Manager/Profile';
import Leads from './components/Manager/Leads';

// Onboarding Flow
import Pseudo from './components/Onboarding/Pseudo';
import Choice from './components/Onboarding/Choice';
import UserTypeSelection from './components/Onboarding/UserTypeSelection';
import TypeBac from './components/Onboarding/TypeBac';
import FormationOnboarding from './components/Onboarding/FormationOnboarding';
import ProblèmesFormationOnboarding from './components/Onboarding/ProblèmesFormationOnboarding';
import Lycee from './components/Onboarding/Lycee';
import ClassementClasse from './components/Onboarding/ClassementClasse';
import GeneralMoyenne from './components/Onboarding/GeneralMoyenne';
import DomainSelection from './components/Onboarding/DomainSelection';
import DomainSelectionPrincipaux from './components/Onboarding/DomainSelectionPrincipaux';
import CitySelection from './components/Onboarding/CitySelection';
import PhoneSelection from './components/Onboarding/PhoneSelection';
import BacGeneral from './components/Onboarding/BacGeneral';
import BacTechnologique from './components/Onboarding/BacTechnologique';
import BacProfessionnel from './components/Onboarding/BacProfessionnel';
import YearStudy from './components/Onboarding/YearStudy';
import MessagePret from './components/MessagePret';
import PresentationFeatures from './components/PresentationFeatures';

// ========== LAZY-LOADED AUTHENTICATED COMPONENTS ==========

// ===== HIGH PRIORITY PRELOAD (webpackPreload: true) =====
// These components are critical for the main user flow and should be preloaded immediately
const AppLayout = lazy(() => import(/* webpackPreload: true, webpackChunkName: "app-layout" */ './AppLayout'));
const SocialRoutesWrapper = lazy(() => import(/* webpackPreload: true, webpackChunkName: "social-routes" */ './SocialRoutesWrapper'));
const ChatDiscussion = lazy(() => import(/* webpackPreload: true, webpackChunkName: "chat-discussion" */ './components/ChatDiscussion'));
const DesktopChatDiscussion = lazy(() => import(/* webpackPreload: true, webpackChunkName: "desktop-chat" */ './components/Desktop/ChatDiscussion'));
const ProtectedClassementOrQuiz = lazy(() => import(/* webpackPreload: true, webpackChunkName: "classement-quiz" */ './utils/ProtectedClassementOrQuiz'));

// ===== LOW PRIORITY PREFETCH (webpackPrefetch: true) =====
// These components are common navigation targets but not immediately critical
const UserProfilePage = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "user-profile" */ './components/Profile/UserProfilePage'));
const Settings = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "settings" */ './components/Settings'));
const DesktopSettings = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "desktop-settings" */ './components/Desktop/Settings'));
const Favoris = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "favoris" */ './components/Favoris'));
const Outils = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "outils" */ './components/Outils'));
const Comparateur = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "comparateur" */ './components/Comparateur'));
const QuizTypeFormation = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "quiz-formation" */ './components/QuizTypeFormation'));

// ===== ON-DEMAND LOADING (No webpack magic comments) =====
// These components are loaded only when specifically needed

// Lettres Flow - Multi-step specific workflow
const LettresSelectFormation = lazy(() => import(/* webpackChunkName: "lettres-select" */ './components/Lettres/SelectFormation'));
const LettresValidateFormation = lazy(() => import(/* webpackChunkName: "lettres-validate" */ './components/Lettres/ValidateFormation'));
const LettresQuestions = lazy(() => import(/* webpackChunkName: "lettres-questions" */ './components/Lettres/Questions'));
const LettresWait = lazy(() => import(/* webpackChunkName: "lettres-wait" */ './components/Lettres/WaitLettre'));
const LettresRelecture = lazy(() => import(/* webpackChunkName: "lettres-relecture" */ './components/Lettres/Relecture'));
const LettresFinal = lazy(() => import(/* webpackChunkName: "lettres-final" */ './components/Lettres/Final'));

// Manager Components - Specific to manager user type
const ManagerProfile = lazy(() => import(/* webpackChunkName: "manager-profile" */ './components/Manager/Profile'));

// Less common or editing components
const ModifySwipeAnswers = lazy(() => import(/* webpackChunkName: "modify-swipe" */ './components/ModifySwipeAnswers'));
const DomainesFavoris = lazy(() => import(/* webpackChunkName: "domaines-favoris" */ './components/Social/DomainesFavoris'));
const UserListPage = lazy(() => import(/* webpackChunkName: "user-list" */ './components/Social/UserListPage'));
const InstagramConnect = lazy(() => import(/* webpackChunkName: "instagram-connect" */ './components/InstagramConnect'));
const DesktopAide = lazy(() => import(/* webpackChunkName: "desktop-aide" */ './components/Desktop/UsersFeedback'));
const UsersFeedBack = lazy(() => import(/* webpackChunkName: "users-feedback" */ './components/UsersFeedback'));

// Utility to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// ==================================================================
// ========================== APP COMPONENT =========================
// ==================================================================

function App() {
  const { loading: isAuthLoading, isAuthenticated, user } = useAuth();
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();

  // --- Global Effects (Loader, Viewport, Theme color, etc.) ---
  useEffect(() => {
    if (!isAuthLoading) {
      const loader = document.getElementById('initial-loader');
      if (loader) {
        loader.classList.add('fade-out');
        setTimeout(() => loader.parentNode?.removeChild(loader), 550);
      }
    }
  }, [isAuthLoading]);
  
  useEffect(() => {
    const updateViewportHeight = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    window.addEventListener('resize', updateViewportHeight);
    updateViewportHeight();
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  useEffect(() => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      let color = theme.colors.background;
      if (location.pathname.startsWith('/classement')) color = theme.colors.white;
      else if (location.pathname.startsWith('/outils')) color = theme.colors.orange_fonce;
      else if (location.pathname === '/social') color = theme.colors.bleu_fonce;
      themeColorMeta.content = color;
    }
  }, [location.pathname]);

  // --- User-specific Effects (App Entry, Phone Number check) ---
  useEffect(() => {
    if (isAuthenticated) {
      apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/increment-app-entry/`, 'POST')
        .then(data => {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const parsed = JSON.parse(storedUser);
            parsed.number_of_app_entries = data.number_of_app_entries;
            localStorage.setItem('user', JSON.stringify(parsed));
          }
        })
        .catch(err => console.error('Error incrementing app entry', err));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const { user_type, phone_number, search_a_school } = user;
      if (user_type !== 'student' && (!phone_number || phone_number.trim() === "") && (search_a_school === true || search_a_school === undefined)) {
        navigate('/profile/modify/phone');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // --- Manager Authentication State ---
  const [isManagerAuthenticated, setIsManagerAuthenticated] = useState(() => {
    // Check for existing token/data in localStorage on initial load
    const token = localStorage.getItem('managerToken');
    const managerData = localStorage.getItem('managerData');
    return !!token && !!managerData;
  });
  const [managerData, setManagerData] = useState(() => {
    const data = localStorage.getItem('managerData');
    return data ? JSON.parse(data) : null;
  });

  const handleManagerLogin = (token, data) => {
    localStorage.setItem('managerToken', token);
    localStorage.setItem('managerData', JSON.stringify(data));
    setManagerData(data);
    setIsManagerAuthenticated(true);
    // Redirect to the manager dashboard after login
    navigate('/manager/analytics');
  };

  const handleManagerLogout = () => {
    localStorage.removeItem('managerToken');
    localStorage.removeItem('managerData');
    setManagerData(null);
    setIsManagerAuthenticated(false);
    navigate('/manager/login');
  };

  // =================================================
  // ============ RENDER LOGIC: THE ROUTER ===========
  // =================================================
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <ScrollToTop />
      <Suspense fallback={<LoadingContainer><LoadingBar /></LoadingContainer>}>
        <Routes>

          {/* ======================= */}
          {/* === MANAGER ROUTES === */}
          {/* ======================= */}
          <Route path="/manager" element={<ManagerLandingPage />} />
          <Route path="/manager/login" element={<ManagerLogin onLoginSuccess={handleManagerLogin} />} />

          {/* Protected Manager Routes */}
          {isManagerAuthenticated ? (
            <>
              <Route path="/manager/analytics" element={<Analytics managerData={managerData} />} />
              <Route path="/manager/update" element={<Update managerData={managerData} />} />
              <Route path="/manager/profile" element={<Profile managerData={managerData} onLogout={handleManagerLogout} />} />
              <Route path="/manager/leads" element={<Leads managerData={managerData} />} />
              {/* Redirect for the old typo'd route */}
              <Route path="/manager/analitycs" element={<Navigate to="/manager/analytics" replace />} />
            </>
          ) : (
             // If not authenticated, any attempt to access protected manager routes will redirect to login.
             // This is a simple way to protect them without a separate wrapper component.
             <Route path="/manager/*" element={<Navigate to="/manager/login" replace />} />
          )}

          {/* ============================= */}
          {/* === REGULAR USER ROUTES === */}
          {/* ============================= */}
          <Route element={ <AppLoadingHandler><Outlet/></AppLoadingHandler> }>

            {/* --- Public & Onboarding Routes --- */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/first-login" element={<SignUp />} />
            <Route path="/formation" element={<FicheFormation />} />
            
            {/* Onboarding Flow */}
            <Route path="/presentation-meo" element={<MeoFirstMessage />} />
            <Route path="/choice" element={<Choice />} />
            <Route path="/pseudo" element={<Pseudo />} />
            <Route path="/user-type-selection" element={<UserTypeSelection />} />
            <Route path="/parent-child-selection" element={<UserTypeSelection />} />
            <Route path="/type-bac" element={<TypeBac />} />
            <Route path="/bac1" element={<FormationOnboarding />} />
            <Route path="/lycee" element={<Lycee />} />
            <Route path="/problemes-formation" element={<ProblèmesFormationOnboarding />} />
            <Route path="/general-moyenne" element={<GeneralMoyenne />} />
            <Route path="/domaines-favoris" element={<DomainSelection />} />
            <Route path="/domaines-principaux" element={<DomainSelectionPrincipaux />} />
            <Route path="/origine-city" element={<CitySelection />} />
            <Route path="/message-pret" element={<MessagePret />} />
            <Route path="/presentation-features" element={<PresentationFeatures />} />
            <Route path="/phone-selection" element={<PhoneSelection />} />
            <Route path="/bac-general" element={<BacGeneral />} />
            <Route path="/bac-technologique" element={<BacTechnologique />} />
            <Route path="/bac-professionnel" element={<BacProfessionnel />} />
            <Route path="/classe-ment" element={<ClassementClasse />} />
            <Route path="/year-study" element={<YearStudy />} />

            {/* --- Public Landing Pages --- */}
            <Route path="/first-page" element={<FirstPage />} />

            {/* --- Root Path Handling --- */}
            <Route path="/" element={<RootRedirect />} />

            {/* --- Authenticated Routes --- */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                {/* DYNAMIC CHAT ROUTE */}
                <Route path="/chat" element={isMobile ? <ChatDiscussion /> : <DesktopChatDiscussion />} />
                <Route path="/profile" element={<UserProfilePage isDesktop={!isMobile} />} />
                <Route path="/profile/:userId" element={<UserProfilePage isDesktop={!isMobile} />} />
                <Route path="/settings" element={isMobile ? <Settings /> : <DesktopSettings />} />
                <Route path="/settings/aide-meo" element={isMobile ? <UsersFeedBack /> : <DesktopAide />} />

                {/* SHARED AUTHENTICATED ROUTES */}
                <Route path="/classement" element={<ProtectedClassementOrQuiz isMobile={isMobile} />} />
                <Route path="/quiz" element={<QuizTypeFormation />} />
                <Route path="/formations-favorites" element={<Favoris />} />
                <Route path="/filieres-favorites" element={<Favoris />} />
                <Route path="/comparateur" element={<Comparateur />} />
                <Route path="/outils" element={<Outils />} />
                <Route path="/lettres" element={<LettresSelectFormation />} />
                <Route path="/lettres-validate" element={<LettresValidateFormation />} />
                <Route path="/lettres-questions" element={<LettresQuestions />} />
                <Route path="/lettres-relecture" element={<LettresRelecture />} />
                <Route path="/lettres-wait" element={<LettresWait />} />
                <Route path="/lettres-final" element={<LettresFinal />} />
                <Route path="/social/*" element={<SocialRoutesWrapper />} />
                <Route path="/instagram-connect" element={<InstagramConnect />} />
                <Route path="/profile/:userId/domaines" element={<DomainesFavoris />} />
                <Route path="/profile/:userId/followers" element={<UserListPage />} />
                <Route path="/profile/:userId/following" element={<UserListPage />} />

                {/* PROFILE MODIFY ROUTES */}
                <Route path="/profile/modify/city" element={<CitySelection />} />
                <Route path="/profile/modify/phone" element={<PhoneSelection />} />
                <Route path="/profile/modify/instagram" element={<InstagramConnect isModifyMode={true} />} />
                <Route path="/profile/modify/domaines-favoris" element={<DomainSelection />} />
                <Route path="/profile/modify/domaines-principaux" element={<DomainSelectionPrincipaux />} />
                <Route path="/profile/modify/lycee" element={<Lycee />} />
                <Route path="/profile/modify/classe-ment" element={<ClassementClasse />} />
                <Route path="/profile/modify/formation" element={<FormationOnboarding />} />
                <Route path="/profile/modify/grades" element={<GeneralMoyenne />} />
                <Route path="/profile/modify/specialites" element={user?.type_bac === 'professionnel' ? <BacProfessionnel /> : user?.type_bac === 'technologique' ? <BacTechnologique /> : user?.type_bac === 'general' ? <BacGeneral /> : <TypeBac />} />
                <Route path="/profile/modify/type-bac" element={<TypeBac />} />
                <Route path="/profile/modify/swipe" element={<ModifySwipeAnswers isMobile={isMobile} />} />
                <Route path="/profile/modify/user-type" element={<UserTypeSelection />} />
                <Route path="/profile/modify/pseudo" element={<Pseudo />} />
              </Route>
            </Route>
            
            {/* --- Fallback for any other route --- */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

const RootRedirect = () => {
  const { isAuthenticated, user } = useAuth();
  const { isMobile } = useDeviceType();

  if (isAuthenticated) {
    const redirectTo = user?.user_type === 'student' 
      ? '/social' 
      : (isMobile ? '/classement' : '/chat');
    return <Navigate to={redirectTo} replace />;
  }

  return isMobile ? <FirstPage /> : <DesktopLandingPage />;
};

export default App;